import type { StripeElementsOptionsClientSecret as StripeJsOptions } from '@stripe/stripe-js';

declare module '@stripe/stripe-js' {
  /**
   * @see https://docs.stripe.com/js/elements_object/create#stripe_elements-options-customPaymentMethods
   * @see https://docs.stripe.com/payments/payment-element/custom-payment-methods#add-cpm-to-elements
   *
   */
  interface StripeElementsOptionsClientSecret {
    customPaymentMethods: {
      id: string;
      options: { type: 'static'; subtitle?: string };
    }[];
  }
}

export type StripeElementsConfig = Pick<
  StripeJsOptions,
  'clientSecret' | 'customerSessionClientSecret' | 'appearance' | 'customPaymentMethods'
> &
  Partial<StripeJsOptions>;

export interface PaymentMethodChangeEvent {
  paymentMethodType: string;
  paymentMethodId?: string;
  complete: boolean;
  empty?: boolean;
}

export interface PaymentMethodResult {
  id: string;
  type: string;
}

export interface ElementsSubmitResult {
  error?: {
    message: string;
    type: string;
  };
  paymentMethodId?: string;
}

export interface CreatePaymentMethodResult {
  paymentMethod?: PaymentMethodResult;
  error?: {
    message: string;
    type: string;
  };
}

export type CreateSetupIntentRequest = unknown;

export interface StripeSetupIntentResponse {
  clientSecret: string;
  customerSessionClientSecret: string;
}

type VerificationMethod = 'INSTANT' | 'MICRODEPOSITS' | 'AUTOMATIC';

export interface CreatePartnerSetupElementRequest {
  partnerPuid: string;
  setAsDefault: boolean;
  countryCode?: string;
  currencyCode?: string;
  returnUrl?: string;
  paymentMethodOptions?: { usBankAccount?: { verificationMethod: VerificationMethod } };
}

export interface CreatePartnerSetupElementResponse {
  setupIntentId: string;
  clientSecret: string;
  customerSessionClientSecret: string;
  customerId: string;
  accountId: string;
}

/**
 * Keep track of the most common payment methods.
 *
 * Stripe models this as a string in their API. We're not using this for typechecking,
 * just for clarity.
 */
export enum CommonPaymentMethods {
  Card = 'card',
  PreAuthorizedDebit = 'acss_debit',
  UsBankAccount = 'us_bank_account',
}
